import React, { useMemo, useState } from "react";
import { ChevronRight, Search } from "lucide-react";
import { clsx } from "clsx";

import { Banner } from "@/design-system/components/Banner/Banner";
import { Input } from "@/design-system/components/Input/Input";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Logo } from "@/design-system/components/Logo/Logo";
import { Typography } from "@/design-system/components/Typography/Typography";
import { SaaSDestination } from "@/destinations/destinations";
import { SaaSDestinationSlug } from "@/destinations/types";
import { getCategoryDisplayName } from "@/destinations/utils/categoryDisplayUtils";
import { getCatalogDestinations } from "@/destinations/v2/CatalogPage/catalogDestinations";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";
import { useAccountEntitlements } from "@/entitlements/queries/useAccountEntitlements";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { useWizards } from "@/wizards/framework/WizardProvider";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { ENABLE_DESTINATION_WIZARD_TYPE } from "../constants";
import { EnableDestinationStepProps } from "../stepProps";
import { getSetupPageReason } from "../wizardSupport";

function useSelectableDestinations() {
  const flag = useFeatureFlags();
  const {
    data: accountEntitlements,
    isLoading,
    isError,
  } = useAccountEntitlements();

  const destinations = useMemo(() => {
    const access = new Set(
      (accountEntitlements?.account_access ?? []).map(
        (entry) => entry.entitlement_key,
      ),
    );
    return (
      getCatalogDestinations(flag)
        .filter((d): d is SaaSDestination => d instanceof SaaSDestination)
        .filter((d) => !d.deprecated)
        // Audience destinations sync segments rather than events, so the
        // event-based steps of this wizard don't apply to them.
        .filter((d) => !d.destinationCategories.includes("Audiences"))
        .filter((d) => access.has(d.key) || access.has("*"))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [accountEntitlements, flag]);

  return { destinations, isLoading, isError };
}

const cardClassName = "flex items-center gap-3 rounded-lg border p-3 text-left";

function CardContent({
  destination,
  subtitle,
}: {
  destination: SaaSDestination;
  subtitle: string;
}) {
  return (
    <>
      <Logo
        brand={destination.slug as SaaSDestinationSlug}
        className="size-8 shrink-0"
      />
      <span className="flex min-w-0 flex-col">
        <span className="text-label-sm text-textPrimary truncate">
          {destination.name}
        </span>
        <span className="text-body-xs text-textTertiary truncate">
          {subtitle}
        </span>
      </span>
    </>
  );
}

export function ChooseDestinationStep({
  data,
  context,
  updateData,
  goNext,
}: EnableDestinationStepProps) {
  const { findInProgressSession, openWizard } = useWizards();
  const [search, setSearch] = useState("");
  const {
    destinations,
    isLoading: entitlementsLoading,
    isError: entitlementsError,
  } = useSelectableDestinations();
  const {
    data: configuredData,
    isLoading: configuredLoading,
    isError: configuredError,
  } = useListAllDestinationsForEnv();
  const { saasDestination: selectedDestination, isHipaaV2 } = context;
  const isLocked = !!data.destinationConfigId;

  const configuredSlugs = useMemo(
    () =>
      new Set(
        (configuredData?.destinations ?? []).map(
          (destination) => destination.type_slug,
        ),
      ),
    [configuredData],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? destinations.filter((d) => d.name.toLowerCase().includes(term))
      : destinations;
  }, [destinations, search]);

  const selectedSetupPageReason = selectedDestination
    ? getSetupPageReason(selectedDestination, { isHipaaV2 })
    : null;

  function isInProgressElsewhere(destination: SaaSDestination): boolean {
    return (
      destination.slug !== data.destinationSlug &&
      !!findInProgressSession(ENABLE_DESTINATION_WIZARD_TYPE, {
        destinationSlug: destination.slug,
      })
    );
  }

  function select(destination: SaaSDestination) {
    if (destination.slug === data.destinationSlug) {
      return;
    }
    // Switching after a save would orphan the saved config, so a different
    // destination gets its own session instead.
    if (isLocked || isInProgressElsewhere(destination)) {
      openWizard(ENABLE_DESTINATION_WIZARD_TYPE, {
        destinationSlug: destination.slug,
      });
      return;
    }
    updateData({
      destinationSlug: destination.slug,
      destinationConfigId: null,
      credentialDraft: {},
      eventToggles: {},
    });
  }

  function renderBody() {
    if (entitlementsLoading || configuredLoading) {
      return <LoadingSpinner className="size-6" />;
    }
    if (entitlementsError || configuredError) {
      return (
        <Banner type="error">
          We couldn’t load your destinations. Refresh the page to try again.
        </Banner>
      );
    }
    return (
      <>
        <div className="grid grid-cols-3 gap-2">
          {filtered.map((destination) => {
            const setupPageReason = getSetupPageReason(destination, {
              isHipaaV2,
            });
            if (setupPageReason) {
              return (
                <WizardLink
                  key={destination.slug}
                  to={destination.configPageUrl()}
                  className={clsx(
                    cardClassName,
                    "border-borderSubtle hover:bg-surface-hover cursor-pointer",
                  )}
                >
                  <CardContent
                    destination={destination}
                    subtitle={setupPageReason}
                  />
                  <ChevronRight
                    aria-hidden
                    className="text-iconMinimal size-icon-sm ml-auto shrink-0"
                  />
                </WizardLink>
              );
            }

            const alreadySetUp =
              !destination.supportsMultiConfig &&
              configuredSlugs.has(destination.slug) &&
              destination.slug !== data.destinationSlug;
            const selected = destination.slug === data.destinationSlug;
            return (
              <button
                key={destination.slug}
                type="button"
                disabled={alreadySetUp}
                aria-pressed={selected}
                onClick={() => select(destination)}
                className={clsx(
                  cardClassName,
                  alreadySetUp
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-surface-hover cursor-pointer",
                  selected
                    ? "border-interactive-selected bg-surface-hover"
                    : "border-borderSubtle",
                )}
              >
                <CardContent
                  destination={destination}
                  subtitle={
                    alreadySetUp
                      ? "Already set up"
                      : isInProgressElsewhere(destination)
                        ? "Setup in progress. Select to resume."
                        : destination.destinationCategories
                            .map(getCategoryDisplayName)
                            .join(", ")
                  }
                />
              </button>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <Typography variant="body-sm" color="subtle" className="text-center">
            {destinations.length === 0 ? (
              <>
                Your plan doesn’t include any destinations yet.{" "}
                <WizardLink
                  to="/destinations/catalog"
                  className="text-textActionSelected cursor-pointer underline"
                >
                  Browse the catalog
                </WizardLink>{" "}
                to request one.
              </>
            ) : (
              `No destinations match “${search}”.`
            )}
          </Typography>
        )}
      </>
    );
  }

  return (
    <WizardStepLayout
      title="Choose a destination"
      description="Search the catalog for the tool you want to send events to."
      primaryAction={{
        label: "Continue",
        onClick: goNext,
        disabled: !data.destinationSlug || !!selectedSetupPageReason,
        disabledReason: selectedSetupPageReason
          ? `Set up ${selectedDestination?.name} on its page`
          : "Choose a destination to continue",
      }}
    >
      {isLocked && selectedDestination && (
        <Banner type="info" kind="note">
          {selectedDestination.name} is connected in this setup. Choosing a
          different destination starts a separate setup.
        </Banner>
      )}
      {selectedDestination && selectedSetupPageReason && (
        <Banner type="warning">
          {selectedDestination.name}: {selectedSetupPageReason}{" "}
          <WizardLink
            to={selectedDestination.configPageUrl()}
            className="cursor-pointer underline"
          >
            Open {selectedDestination.name} setup
          </WizardLink>
        </Banner>
      )}
      <Input
        autoFocus
        label="Search destinations"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        leadingIcon={<Search aria-hidden />}
      />
      {renderBody()}
    </WizardStepLayout>
  );
}
